import { osnovaApi } from '../base';

// ОСНОВА.Инженер — №7 «Анализ комплектности РД» (docs/pto-rd-gaps-spec.md).
// Слои без ИИ: типовой комплект, нормы ГЭСН («норма × объём» — оценка, не для
// заказа), сверка ВОР↔СП. Прогон идёт в очереди ПТО секунды — вкладка
// опрашивает состояние, пока есть `pending`. Принятое — только справочный лист
// «Дополнительно (нет в РД)» ведомости и раздел автозапроса SQ (решение 11).
//
// ИИ-слой — только по кнопке (решение 12): оценка (`ai/estimate`) → цена →
// подтверждение (`ai/run` с `confirm_usd_micro`). Предложения ИИ — находки
// слоя `ai` с цитатой из РД или шифром нормы, без количества; каждое проверено
// Flash и программой (цитата сверена с текстом листа).

/** Флаг `OSNOVA_PTO_RD_GAPS` для проекта и пользователя. */
export interface PtoRdGapsConfig {
  /** Вкладка видна (on — всем, dry — пилотным проектам и «Инженер+»). */
  enabled: boolean;
  mode: 'off' | 'dry' | 'on';
  /** «Сохранить как типовой комплект» — не ниже «Инженер+». */
  can_save_rules: boolean;
  qty_note: string;
  /** Кнопка «Спросить ИИ» (ИИ-слой подключён и не выключен флагом). */
  ai_enabled?: boolean;
  /** Лимит прогона ИИ, микродоллары, и вызовов. */
  ai_max_usd_micro?: number;
  ai_max_calls?: number;
}

export type PtoRdGapRunStatus = 'queued' | 'running' | 'done' | 'partial' | 'failed';

/** Прогон анализа (полный — с `summary`). */
export interface PtoRdGapRun {
  id: string;
  project_id: string;
  revision_id: string;
  layers: string[];
  status: PtoRdGapRunStatus;
  mode: 'dry' | 'on';
  version: string;
  norm_edition: string | null;
  input_hash: string | null;
  est_cost_usd_micro: number;
  cost_usd_micro: number;
  input_tokens: number;
  output_tokens: number;
  llm_calls: number;
  summary: Record<string, unknown>;
  error_message: string | null;
  started_by: string | null;
  created_at: string;
  started_at: string | null;
  finished_at: string | null;
}

/** Прогон без сводки — история и «идёт проверка». */
export interface PtoRdGapRunBrief {
  id: string;
  status: PtoRdGapRunStatus;
  mode: 'dry' | 'on';
  version: string;
  created_at: string;
  started_at: string | null;
  finished_at: string | null;
  started_by: string | null;
  error_message: string | null;
  findings: { total: number; visible: number; hidden: number } | null;
  layers?: string[];
  /** Прогон ИИ: оценка до запуска и расход, микродоллары. */
  est_cost_usd_micro?: number;
  cost_usd_micro?: number;
  llm_calls?: number;
  /** Ход идущего прогона ИИ. */
  progress?: { done: number; total: number; current?: string; usd_micro?: number } | null;
}

export type PtoRdGapLayer = 'rule' | 'norm' | 'reconcile' | 'ai';
export type PtoRdGapKind =
  | 'aux_material'
  | 'kit_material'
  | 'missing_material'
  | 'missing_work'
  | 'work_without_material'
  | 'material_without_work'
  | 'qty_mismatch'
  | 'incomparable';
export type PtoRdGapStatus = 'proposed' | 'doubtful' | 'accepted' | 'rejected' | 'stale';

/** Находка прогона. `evidence` / `norm_refs` — JSON доказательств (строки
 *  ВОР/СП, шифры норм с расходом, наличие в СП). */
export interface PtoRdGapFinding {
  id: string;
  run_id: string;
  project_id: string;
  revision_id: string;
  finding_key: string;
  layer: PtoRdGapLayer;
  kind: PtoRdGapKind;
  group_key: string;
  group_label: string;
  name: string;
  unit: string | null;
  qty_estimate: number | null;
  qty_note: string | null;
  resource_code: string | null;
  resource_class: string | null;
  visibility: 'show' | 'hidden_consumable' | 'hidden_obsolete';
  presence: 'absent' | 'unclear' | null;
  norm_refs: unknown;
  evidence: unknown;
  related_record_uids: string[];
  status: PtoRdGapStatus;
  decided_by: string | null;
  decided_at: string | null;
  note: string | null;
  sq_request_id: string | null;
  created_at: string;
}

/** Состояние вкладки ревизии. */
export interface PtoRdGapsOverview {
  /** Последний завершённый прогон (его находки ниже). */
  run: PtoRdGapRun | null;
  /** Идущий прогон (опрашиваем, пока не завершится). */
  pending: PtoRdGapRunBrief | null;
  runs: PtoRdGapRunBrief[];
  /** Позиции РД, нормы или правила изменились после прогона. */
  stale: boolean;
  /** Значки «⚠ N» у строк ВОР: record_uid → N. */
  row_flags: Record<string, number>;
  findings: PtoRdGapFinding[];
  qty_note: string;
  /** ИИ-слой: последний прогон ИИ (его находки — в `findings`), идущий. */
  ai?: PtoRdGapsAiState;
}

/** Состояние ИИ-слоя на вкладке. */
export interface PtoRdGapsAiState {
  available: boolean;
  run: PtoRdGapRun | null;
  pending: PtoRdGapRunBrief | null;
  runs: PtoRdGapRunBrief[];
  /** Позиции РД изменились после прогона ИИ. */
  stale: boolean;
  max_usd_micro: number;
  max_calls: number;
  qty_note: string;
}

/** Оценка вызовов модели. */
export interface PtoRdGapAiEstimate {
  calls: number;
  input_tokens: number;
  output_tokens: number;
  usd_micro: number;
}

/** Группа для вопроса ИИ: комплект целиком, вид работ, система. */
export interface PtoRdGapAiGroup {
  key: string;
  kind: 'set' | 'work' | 'system';
  label: string;
  rows: number;
  input_tokens: number;
  estimate: PtoRdGapAiEstimate;
  /** Ответ ИИ есть и вход не менялся — спрашивать незачем (0 $). */
  unchanged: boolean;
  asked_in_run: string | null;
}

/** План ИИ-прогона ревизии. */
export interface PtoRdGapAiPlan {
  prompt_version: string;
  groups: PtoRdGapAiGroup[];
  norm_choice: {
    rows: number;
    unique: number;
    estimate: PtoRdGapAiEstimate;
    unchanged: boolean;
  };
  notes_pages: number[];
  notes_tokens: number;
  notes_truncated: boolean;
  vor_items: number;
  sp_items: number;
  max_usd_micro: number;
  max_calls: number;
}

export interface PtoRdGapAiRequest {
  groups: string[];
  norm_choice: boolean;
}

export interface PtoRdGapAiSelection {
  request: PtoRdGapAiRequest;
  estimate: PtoRdGapAiEstimate;
  allowed: boolean;
  reason: string | null;
}

/** Правило типового комплекта (без количеств). */
export interface PtoRdGapRule {
  id: string;
  work_key: string;
  work_label: string;
  match_terms: string[];
  material_name: string;
  material_class: string;
  resource_code: string | null;
  unit: string | null;
  note: string | null;
  source_project_id: string | null;
  source_finding_id: string | null;
  active: boolean;
  created_by: string | null;
  created_at: string;
  deactivated_by: string | null;
  deactivated_at: string | null;
}

export const ptoRdGapsApi = osnovaApi.injectEndpoints({
  endpoints: (b) => ({
    getPtoRdGapsConfig: b.query<PtoRdGapsConfig, string>({
      query: (projectId) => `/agents/pto/projects/${projectId}/rd-gaps/config`,
      providesTags: ['PtoRdGaps'],
    }),
    getPtoRdGaps: b.query<PtoRdGapsOverview, string>({
      query: (revisionId) => `/agents/pto/revisions/${revisionId}/rd-gaps`,
      providesTags: (_r, _e, revisionId) => [{ type: 'PtoRdGaps', id: revisionId }],
    }),
    /** «Проверить по нормам»: прогон без ИИ в очередь ПТО. */
    runPtoRdGaps: b.mutation<{ run: PtoRdGapRun; created: boolean }, string>({
      query: (revisionId) => ({
        url: `/agents/pto/revisions/${revisionId}/rd-gaps/run`,
        method: 'POST',
      }),
      invalidatesTags: (_r, _e, revisionId) => [{ type: 'PtoRdGaps', id: revisionId }],
    }),
    /** «Спросить ИИ» — оценка: группы с ценой, лимиты, оценка выбранного.
     *  Модель не вызывается. */
    estimatePtoRdGapsAi: b.mutation<
      { plan: PtoRdGapAiPlan; selection: PtoRdGapAiSelection },
      { revisionId: string } & PtoRdGapAiRequest
    >({
      query: ({ revisionId, ...body }) => ({
        url: `/agents/pto/revisions/${revisionId}/rd-gaps/ai/estimate`,
        method: 'POST',
        body,
      }),
    }),
    /** «Спросить ИИ» — запуск с подтверждённой ценой (409 — цена изменилась). */
    runPtoRdGapsAi: b.mutation<
      { run: PtoRdGapRun; created: boolean; selection: PtoRdGapAiSelection },
      { revisionId: string; confirm_usd_micro: number } & PtoRdGapAiRequest
    >({
      query: ({ revisionId, ...body }) => ({
        url: `/agents/pto/revisions/${revisionId}/rd-gaps/ai/run`,
        method: 'POST',
        body,
      }),
      invalidatesTags: (_r, _e, arg) => [{ type: 'PtoRdGaps', id: arg.revisionId }],
    }),
    listPtoRdGapRuns: b.query<{ items: PtoRdGapRun[] }, string>({
      query: (revisionId) => `/agents/pto/revisions/${revisionId}/rd-gaps/runs`,
      providesTags: (_r, _e, revisionId) => [{ type: 'PtoRdGaps', id: revisionId }],
    }),
    decidePtoRdGapFinding: b.mutation<
      { finding: PtoRdGapFinding },
      {
        findingId: string;
        revisionId: string;
        decision: 'accept' | 'reject' | 'reset';
        note?: string;
      }
    >({
      query: ({ findingId, decision, note }) => ({
        url: `/agents/pto/rd-gap-findings/${findingId}/decide`,
        method: 'POST',
        body: { decision, note },
      }),
      invalidatesTags: (_r, _e, arg) => [{ type: 'PtoRdGaps', id: arg.revisionId }],
    }),
    /** «Сохранить как типовой комплект» (не ниже «Инженер+»). */
    savePtoRdGapRule: b.mutation<
      { rule: PtoRdGapRule; created: boolean },
      { findingId: string; revisionId: string }
    >({
      query: ({ findingId }) => ({
        url: `/agents/pto/rd-gap-findings/${findingId}/save-rule`,
        method: 'POST',
      }),
      invalidatesTags: (_r, _e, arg) => ['PtoRdGapRule', { type: 'PtoRdGaps', id: arg.revisionId }],
    }),
    listPtoRdGapRules: b.query<{ items: PtoRdGapRule[] }, { all?: boolean } | void>({
      query: (arg) => `/agents/pto/rd-gap-rules${arg && arg.all ? '?all=true' : ''}`,
      providesTags: ['PtoRdGapRule'],
    }),
    deactivatePtoRdGapRule: b.mutation<{ ok: boolean }, string>({
      query: (ruleId) => ({
        url: `/agents/pto/rd-gap-rules/${ruleId}/deactivate`,
        method: 'POST',
      }),
      invalidatesTags: ['PtoRdGapRule', 'PtoRdGaps'],
    }),
  }),
});

export const {
  useGetPtoRdGapsConfigQuery,
  useGetPtoRdGapsQuery,
  useRunPtoRdGapsMutation,
  useEstimatePtoRdGapsAiMutation,
  useRunPtoRdGapsAiMutation,
  useListPtoRdGapRunsQuery,
  useDecidePtoRdGapFindingMutation,
  useSavePtoRdGapRuleMutation,
  useListPtoRdGapRulesQuery,
  useDeactivatePtoRdGapRuleMutation,
} = ptoRdGapsApi;
